/**
 * Geo Pricing Handler
 * Modifies product prices based on geo-location pricing
 */

// Logger utility for consistent logging
class Logger {
  static debug(message, data = null) {
    if (window.localStorage.getItem('DEBUG_GEO_PRICING')) {
      console.log(`🌍 [GeoPricing] ${message}`, data || '');
    }
  }

  static error(message, error = null) {
    console.error(`❌ [GeoPricing] ${message}`, error || '');
  }
}

class GeoPricingHandler {
  constructor() {
    this.selectors = {
      priceContainer: '[data-price-container]',
      price: '[data-price]',
      priceMin: '[data-price-min]',
      priceMax: '[data-price-max]',
      compareAtPrice: '[data-price-compare]',
      productForm: '[data-product-form]',
      variantSelect: '[data-variants]',
      productOptions: '[data-variant-option]',
      productPricing: '[data-product-pricing]',
      hiddenCurrentPrice: '[data-current-price-hidden]',
      hiddenComparePrice: '[data-compare-price-hidden]',
      hiddenCurrentPriceRange: '[data-current-price-range-hidden]',
      hiddenComparePriceRange: '[data-compare-price-range-hidden]',
      productDetails: '[data-product-details]',
      optionsSelection: 'options-selection',
      variantSelection: 'variant-selection',
      quickShop: '.product__container--quick-shop',
      quickShopPricing: '[data-product-pricing]',
      modalContent: '[data-modal-content]',
      modal: '.modal',
      addToCartForm: 'form[action*="/cart/add"]'
    };

    this.isUpdatingPrice = false;
    this.isLoggedIn = false;
    this.observers = new Set();
    this.init();
  }

  async init() {
    try {
      this.initializeLoadingState();
      this.setupEventListeners();
      this.checkExistingPricing();
      this.observeQuickShop();
      this.setupCartIntegration();

      // Check login status after initialization
      this.isLoggedIn = await this.checkUserLoginStatus();
      if (this.isLoggedIn) {
        Logger.debug('User is logged in, applying pricing');
        if (this.currentDiscount) {
          this.applyPricing(this.currentDiscount.value, this.currentDiscount.region);
        } else {
          this.applyPricing(0);
        }
      }
    } catch (error) {
      Logger.error('Initialization error:', error);
    }
  }

  setupCartIntegration() {
    document.addEventListener('submit', this.handleCartSubmit.bind(this));
    this.initializeCartAttributes();
  }

  async handleCartSubmit(event) {
    const form = event.target.closest(this.selectors.addToCartForm);
    if (!form) return;

    event.preventDefault();

    try {
      const formData = new FormData(form);
      const cartResponse = await this.addToCart(formData);
      await this.updateCartAttributes();
      this.dispatchCartEvent('product:added', { product: cartResponse });
    } catch (error) {
      Logger.error('Cart submission error:', error);
      this.dispatchCartEvent('product:error', { error: error.message });
    }
  }

  async addToCart(formData) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: formData
    });

    const data = await response.json();
    if (data.status === 422) {
      throw new Error(data.description || 'Error adding to cart');
    }
    return data;
  }

  async updateCartAttributes() {
    const attributes = {
      geolocation: this.currentDiscount?.region !== 'Default' ? this.currentDiscount?.region : null
    };

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes })
    });
  }

  dispatchCartEvent(eventName, detail) {
    document.dispatchEvent(new CustomEvent(eventName, { detail }));
  }

  async initializeCartAttributes() {
    try {
      await this.updateCartAttributes();
    } catch (error) {
      Logger.error('Error setting initial cart attributes:', error);
    }
  }

  observeQuickShop() {
    // Create a mutation observer to watch for quickshop content
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        // Check for modal content changes
        const modalContent = mutation.target.matches(this.selectors.modalContent) ?
          mutation.target : mutation.target.querySelector(this.selectors.modalContent);

        if (modalContent) {
          const quickshopContent = modalContent.querySelector(this.selectors.quickShopPricing);
          if (quickshopContent && !quickshopContent.hasAttribute('data-geo-pricing-initialized')) {
            // Wait briefly for the content to be fully rendered
            setTimeout(() => {
              this.initializeQuickShop(quickshopContent);
            }, 100);
          }
        }

        // Check for direct pricing content changes
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) { // Element node
              const pricingContent = node.matches(this.selectors.quickShopPricing) ?
                node : node.querySelector(this.selectors.quickShopPricing);

              if (pricingContent && !pricingContent.hasAttribute('data-geo-pricing-initialized')) {
                setTimeout(() => {
                  this.initializeQuickShop(pricingContent);
                }, 100);
              }
            }
          });
        }
      }
    });

    // Start observing the document with a more specific configuration
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    // Also observe modal visibility changes
    document.querySelectorAll(this.selectors.modal).forEach(modal => {
      const modalObserver = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          if (mutation.type === 'attributes' &&
              (mutation.attributeName === 'style' || mutation.attributeName === 'class')) {
            const pricingContent = modal.querySelector(this.selectors.quickShopPricing);
            if (pricingContent && !pricingContent.hasAttribute('data-geo-pricing-initialized')) {
              setTimeout(() => {
                this.initializeQuickShop(pricingContent);
              }, 100);
            }
          }
        });
      });

      modalObserver.observe(modal, {
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    });
  }

  initializeQuickShop(quickshopContent) {
    if (!quickshopContent || quickshopContent.hasAttribute('data-geo-pricing-initialized')) return;

    quickshopContent.setAttribute('data-geo-pricing-initialized', 'true');

    // Initialize loading state for quickshop prices
    const containers = quickshopContent.querySelectorAll(this.selectors.priceContainer);
    if (containers.length === 0) return; // No price containers found

    containers.forEach(container => {
      if (!container.querySelector('.price-loading')) {
        const loader = document.createElement('span');
        loader.className = 'price-loading';
        container.appendChild(loader);
      }
      container.classList.remove('geo-pricing-ready');
    });

    // Always apply pricing if we have either a regional discount or user is logged in
    if (this.currentDiscount || this.isLoggedIn) {
      const discountValue = this.currentDiscount?.value || 0;
      const region = this.currentDiscount?.region;

      // Force immediate price update for quickshop
      requestAnimationFrame(() => {
        this.applyPricingToContainer(quickshopContent, discountValue, region);
      });
    } else {
      this.markContainerPricingAsReady(quickshopContent);
    }

    // Also listen for the initial variant selection event
    const variantSelect = quickshopContent.querySelector(this.selectors.variantSelect);
    if (variantSelect) {
      // Trigger a variant change event to ensure prices are updated
      const event = new CustomEvent('variant:change', {
        target: variantSelect
      });
      this.handleVariantChange(event);
    }
  }

  applyPricingToContainer(container, discountPercentage, region) {
    if (!container) return;

    const priceContainers = container.querySelectorAll(this.selectors.priceContainer);
    priceContainers.forEach(priceContainer => {
      this.updatePriceElements(priceContainer, discountPercentage);
      this.addRegionText(priceContainer, region);
    });

    // Update hidden prices within this container
    const hiddenElements = [
      ...container.querySelectorAll(this.selectors.hiddenCurrentPrice),
      ...container.querySelectorAll(this.selectors.hiddenComparePrice),
      ...container.querySelectorAll(this.selectors.hiddenCurrentPriceRange),
      ...container.querySelectorAll(this.selectors.hiddenComparePriceRange)
    ];

    hiddenElements.forEach(el => {
      const priceEl = el?.querySelector('[data-price]');
      if (priceEl?.textContent.trim()) {
        this.updatePrice(priceEl, discountPercentage);
      }
    });

    this.markContainerPricingAsReady(container);
  }

  markContainerPricingAsReady(container) {
    if (!container) return;

    const priceContainers = container.querySelectorAll(this.selectors.priceContainer);
    priceContainers.forEach(priceContainer => {
      priceContainer.classList.add('geo-pricing-ready');
      const loader = priceContainer.querySelector('.price-loading');
      if (loader) loader.remove();
    });
  }

  initializeLoadingState() {
    document.querySelectorAll(this.selectors.priceContainer).forEach(container => {
      if (!container.querySelector('.price-loading')) {
        const loader = document.createElement('span');
        loader.className = 'price-loading';
        container.appendChild(loader);
      }
      container.classList.remove('geo-pricing-ready');
    });
  }

  setupEventListeners() {
    // Listen for geo pricing updates
    document.addEventListener('geoPricingReceived', (event) => {
      const { success, discount_value, discount_region } = event.detail;
      // Always store the current discount info
      this.currentDiscount = { value: discount_value, region: discount_region };

      // Always apply pricing to handle both regional and member discounts
      this.applyPricing(discount_value, discount_region);
    });

    // Listen for variant changes
    document.addEventListener('variant:change', (event) => {
      // Always handle variant changes if we have pricing info or user is logged in
      if (this.currentDiscount || this.isLoggedIn) {
        this.handleVariantChange(event);
      }
    });

    document.addEventListener('variant:price:update', (event) => {
      // Always handle price updates if we have pricing info or user is logged in
      if (this.currentDiscount || this.isLoggedIn) {
        this.handleVariantChange(event);
      }
    });

    // Listen for quickshop events
    document.addEventListener('quickshop:loaded', (event) => {
      const quickshopContent = event.target.querySelector(this.selectors.quickShopPricing);
      if (quickshopContent) {
        this.initializeQuickShop(quickshopContent);
      }
    });

    // Listen for option changes with event delegation
    document.addEventListener('change', (event) => {
      const optionElement = event.target.closest(this.selectors.optionsSelection);
      if (optionElement && (this.currentDiscount || this.isLoggedIn)) {
        this.handleVariantChange({ target: optionElement });
      }
    }, true);
  }

  checkExistingPricing() {
    if (window.EmpireState?.geoPricing?.success) {
      const { discount_value, discount_region } = window.EmpireState.geoPricing;
      // Always store the current discount, even if it's default region
      this.currentDiscount = { value: discount_value, region: discount_region };

      // Apply pricing in all cases - this will handle both regional and login discounts
      this.applyPricing(discount_value, discount_region);
    } else {
      this.markPricingAsReady();
    }
  }

  handleVariantChange(event) {
    // Modified to handle cases where only member discount applies
    if (!this.currentDiscount && !this.isLoggedIn) return;

    // Find the closest price container to the variant that changed
    let container = event.target?.closest('[data-product-pricing]');

    // If no container found directly, try to find it in the quickshop
    if (!container && event.target) {
      const quickshop = event.target.closest(this.selectors.quickShop);
      if (quickshop) {
        container = quickshop.querySelector('[data-product-pricing]');
      }
    }

    if (container) {
      const priceContainers = container.querySelectorAll(this.selectors.priceContainer);
      priceContainers.forEach(priceContainer => {
        priceContainer.classList.remove('geo-pricing-ready');
      });

      // Apply pricing only to this container
      requestAnimationFrame(() => {
        this.applyPricingToContainer(container, this.currentDiscount?.value || 0, this.currentDiscount?.region);
      });
    } else {
      // Fallback to updating all prices if container not found
      requestAnimationFrame(() => {
        this.applyPricing(this.currentDiscount?.value || 0, this.currentDiscount?.region);
      });
    }
  }

  markPricingAsReady() {
    document.querySelectorAll(this.selectors.priceContainer).forEach(container => {
      container.classList.add('geo-pricing-ready');
      const loader = container.querySelector('.price-loading');
      if (loader) loader.remove();
    });
  }

  applyPricing(discountPercentage, region) {
    if (this.isUpdatingPrice) return;
    this.isUpdatingPrice = true;

    try {
      document.querySelectorAll(this.selectors.priceContainer).forEach(container => {
        this.updatePriceElements(container, discountPercentage);
        this.addRegionText(container, region);
      });

      this.updateHiddenPrices(discountPercentage);
      this.markPricingAsReady();
    } finally {
      this.isUpdatingPrice = false;
    }
  }

  updatePriceElements(container, discountPercentage) {
    // Update regular price
    const priceEl = container.querySelector(this.selectors.price);
    if (priceEl) this.updatePrice(priceEl, discountPercentage);

    // Update price range
    const minPriceEl = container.querySelector(this.selectors.priceMin);
    const maxPriceEl = container.querySelector(this.selectors.priceMax);
    if (minPriceEl && maxPriceEl) {
      this.updatePrice(minPriceEl, discountPercentage);
      this.updatePrice(maxPriceEl, discountPercentage);
    }

    // Update compare at price
    const compareAtPriceEl = container.querySelector(this.selectors.compareAtPrice);
    if (compareAtPriceEl) this.updatePrice(compareAtPriceEl, discountPercentage);
  }

  updatePrice(element, discountPercentage) {
    if (!element) return;

    const originalText = element.textContent.trim();
    if (!originalText) return;

    const originalPrice = parseFloat(originalText.replace(/[^0-9.]/g, ''));
    if (isNaN(originalPrice) || originalPrice === 0) return;

    let finalPrice = originalPrice;

    // Apply regional discount if available
    if (discountPercentage > 0) {
      const regionalDiscountMultiplier = (100 - discountPercentage) / 100;
      finalPrice = finalPrice * regionalDiscountMultiplier;
    }

    // Apply additional 5% discount for logged-in users
    if (this.isLoggedIn) {
      const loginDiscountMultiplier = 0.95; // 5% off
      finalPrice = finalPrice * loginDiscountMultiplier;
    }

    element.textContent = this.formatPrice(finalPrice);
  }

  updateHiddenPrices(discountPercentage) {
    const hiddenElements = [
      ...document.querySelectorAll(this.selectors.hiddenCurrentPrice),
      ...document.querySelectorAll(this.selectors.hiddenComparePrice),
      ...document.querySelectorAll(this.selectors.hiddenCurrentPriceRange),
      ...document.querySelectorAll(this.selectors.hiddenComparePriceRange)
    ];

    hiddenElements.forEach(el => {
      const priceEl = el?.querySelector('[data-price]');
      if (priceEl?.textContent.trim()) {
        this.updatePrice(priceEl, discountPercentage);
      }
    });
  }

  addRegionText(container, region) {
    const existingText = container.querySelector('.geo-region-text');
    if (existingText) existingText.remove();

    const regionText = document.createElement('span');
    regionText.className = 'geo-region-text';

    let discountText = '';
    if (region && region !== 'Default') {
      discountText = `Price for ${region} region`;
    }

    if (this.isLoggedIn) {
      if (discountText) {
        discountText += ' + ';
      }
      discountText += 'Includes 5% Member Discount';
    }

    if (discountText) {
      regionText.textContent = discountText;
      container.appendChild(regionText);
    }
  }

  formatPrice(price) {
    return `$${price.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  }

  async checkUserLoginStatus() {
    try {
      // Check multiple indicators of login status
      const isLoggedIn = Boolean(
        window.Shopify?.customer ||
        document.body.classList.contains('customer-logged-in') ||
        (document.querySelector('a[href="/account"]') && !document.querySelector('a[href="/account/login"]'))
      );

      Logger.debug(`Login status check: ${isLoggedIn ? 'Logged in' : 'Not logged in'}`);
      return isLoggedIn;
    } catch (error) {
      Logger.error('Login status check error:', error);
      return false;
    }
  }

  // Cleanup method to remove observers
  destroy() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => new GeoPricingHandler());
